import React, { useState } from 'react';
import { TextField, Button, Box, Typography, Card, CardContent, Alert, Stepper, Step, StepLabel } from '@mui/material';
import { apiFetch } from './api';
import { 
  registerWebAuthn, 
  setupTOTP, 
  verifyTOTPSetup, 
  isWebAuthnSupported 
} from './webauthn';

export default function CreateAdmin({ onCreated }) {
  const [step, setStep] = useState(0); // 0: create account, 1: choose MFA, 2: setup MFA
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaMethod, setMfaMethod] = useState(''); // 'webauthn', 'totp', or 'skip'
  const [totpData, setTotpData] = useState(null);
  const [totpCode, setTotpCode] = useState('');

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiFetch('/setup-admin', {
        method: 'POST',
        body: { username, password }
      });
      setSuccess('Admin account created! Now set up multi-factor authentication for better security.');
      setStep(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupWebAuthn = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await registerWebAuthn(username);
      setSuccess('WebAuthn credential registered successfully! You can now log in with your passkey or security key.');
      setTimeout(() => onCreated(), 2000);
    } catch (err) {
      setError(`WebAuthn setup failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupTOTP = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await setupTOTP(username);
      setTotpData(data);
      setStep(2);
      setSuccess('Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)');
    } catch (err) {
      setError(`TOTP setup failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await verifyTOTPSetup(totpCode);
      setSuccess('TOTP enabled successfully! You can now log in with your authenticator app.');
      setTimeout(() => onCreated(), 2000);
    } catch (err) {
      setError(`Verification failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipMFA = () => {
    setSuccess('Setup complete. You can add MFA later in your account settings.');
    setTimeout(() => onCreated(), 1500);
  };

  const steps = ['Create Account', 'Choose MFA Method', 'Setup MFA'];

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 3 }}>
      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0: Create Admin Account */}
      {step === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>Create Admin Account</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create the initial administrator account for ChessCrew
            </Typography>
            <form onSubmit={handleCreateAdmin}>
              <TextField
                label="Username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                helperText="You can use MFA instead of password in the next step"
              />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                fullWidth 
                disabled={loading} 
                sx={{ mt: 2 }}
              >
                {loading ? 'Creating...' : 'Create Admin Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Choose MFA Method */}
      {step === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>Set Up Multi-Factor Authentication</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add an extra layer of security to your account
            </Typography>

            {isWebAuthnSupported() && (
              <Card variant="outlined" sx={{ mb: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={handleSetupWebAuthn}>
                <CardContent>
                  <Typography variant="h6">🔐 WebAuthn (Passkey/Security Key)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use biometric authentication, USB security keys (YubiKey), or platform authenticators
                  </Typography>
                  <Button variant="contained" color="primary" sx={{ mt: 2 }} disabled={loading}>
                    {loading ? 'Setting up...' : 'Set Up WebAuthn'}
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card variant="outlined" sx={{ mb: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={handleSetupTOTP}>
              <CardContent>
                <Typography variant="h6">📱 TOTP (Authenticator App)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use Google Authenticator, Authy, or similar apps for 6-digit codes
                </Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }} disabled={loading}>
                  {loading ? 'Setting up...' : 'Set Up TOTP'}
                </Button>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={handleSkipMFA}>
              <CardContent>
                <Typography variant="h6">⏭️ Skip for Now</Typography>
                <Typography variant="body2" color="text.secondary">
                  Continue without MFA (you can add it later)
                </Typography>
                <Button variant="outlined" sx={{ mt: 2 }}>
                  Skip MFA Setup
                </Button>
              </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Setup TOTP */}
      {step === 2 && totpData && (
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>Set Up TOTP Authentication</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Scan this QR code with your authenticator app
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <img src={totpData.qr_code} alt="TOTP QR Code" style={{ maxWidth: '100%' }} />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Manual entry:</strong> {totpData.secret}
              </Typography>
            </Alert>

            <form onSubmit={handleVerifyTOTP}>
              <TextField
                label="Enter 6-digit code"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value)}
                fullWidth
                margin="normal"
                required
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                helperText="Enter the code from your authenticator app to verify"
              />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                fullWidth 
                disabled={loading} 
                sx={{ mt: 2 }}
              >
                {loading ? 'Verifying...' : 'Verify and Complete Setup'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
